import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  acceptedLogoInputTypes,
  allowedLogoFileTypes
} from '@components/file-upload/constants';
import { Avatar, Button, Icon } from '@components/index';
import {
  allowedImageSize,
  allowedImageSizeLabel,
  isWideLogoRatio
} from '@constants/file';
import { imageInitialState } from '@constants/generic';
import testId from '@constants/testId';
import useDragAndDrop from '@hooks/useDragAndDrop';
import { ImageForm, LogoRatio } from '@types';

import ImageCropModal from './ImageCropModal';
import LogoDeleteIcon from './LogoDeleteIcon';

import theme from '../../../themes/colors';

interface FileUploadProps {
  orgName: string;
  handleAdd: (file: File) => void;
  handleRemove: () => void;
  setOrgImage: Dispatch<SetStateAction<ImageForm>>;
  setOpenImageCropModal: Dispatch<SetStateAction<boolean>>;
  label: string;
  initialPreviewUrl?: string;
  openImageCropModal: boolean;
  uploadedFile: File | undefined | null;
  onChangeLogoClick?: () => void;
  logoRatio: LogoRatio;
}

const LogoUpload: FC<FileUploadProps> = ({
  orgName,
  handleAdd,
  setOrgImage,
  handleRemove,
  setOpenImageCropModal,
  label,
  uploadedFile,
  openImageCropModal,
  initialPreviewUrl = '',
  onChangeLogoClick,
  logoRatio
}) => {
  const { fileDropError, setFileDropError } = useDragAndDrop();

  const inputId = `${label}_input_id`;

  const [fileName, setFileName] = useState<string>('');
  const [fileUrl, setFileUrl] = useState<string | undefined>();
  const [file, setFile] = useState<File | undefined | null>(uploadedFile);
  const [previewUrl, setPreviewUrl] = useState<string>(initialPreviewUrl);
  const [ratio, setRatio] = useState<LogoRatio>(logoRatio);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValidFile = (selectedFile: File) => {
    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'jpeg') {
      const error = 'Upload failed. Unsupported file type';
      setFileDropError(error);
      return false;
    }

    if (selectedFile.size > allowedImageSize) {
      const error = `Upload failed. File size more than ${allowedImageSizeLabel}`;
      setFileDropError(error);
      return false;
    }

    if (
      selectedFile.type &&
      !allowedLogoFileTypes.includes(selectedFile.type)
    ) {
      const error = 'Upload failed. Unsupported file type';
      setFileDropError(error);
      return false;
    }

    return true;
  };

  const handleFileProcessing = async (selectedFile: File) => {
    setFileDropError('');
    setFileName(selectedFile.name);
    setFileUrl(URL.createObjectURL(selectedFile));
    setOpenImageCropModal(true);
  };

  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile && isValidFile(selectedFile)) {
      handleFileProcessing(selectedFile);
    }
  };

  const deleteFile = () => {
    setOrgImage(imageInitialState);
    setFile(undefined);
    setPreviewUrl('');
    handleRemove();
  };

  const onCancelCrop = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName('');
    setFileUrl('');
    setOpenImageCropModal(false);
  };

  const onConfirmCrop = (croppedFile: File, croppedRatio: LogoRatio) => {
    const previewUrls = URL.createObjectURL(croppedFile);
    setRatio(croppedRatio);
    setFile(croppedFile);
    setPreviewUrl(previewUrls);
    setOrgImage({
      file: croppedFile,
      preview: previewUrls,
      ratio: croppedRatio
    });
    setOpenImageCropModal(false);
    handleAdd(croppedFile);
  };

  useEffect(() => {
    if (uploadedFile) setPreviewUrl(URL.createObjectURL(uploadedFile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  useEffect(() => {
    if (initialPreviewUrl) setPreviewUrl(initialPreviewUrl);
  }, [initialPreviewUrl]);

  useEffect(() => {
    setRatio(logoRatio);
  }, [logoRatio]);

  return (
    <div className="px-6 pb-2 pt-3">
      <div className="flex flex-col gap-1">
        <div className="relative flex gap-3">
          <div className="group relative">
            <Avatar
              imageId={testId.header.organizationLogo}
              name={orgName}
              imageUrl={file ? URL.createObjectURL(file) : previewUrl}
              imageClassName="rounded-xl"
              className={`${
                previewUrl && isWideLogoRatio(ratio)
                  ? 'h-28 w-56 min-w-56'
                  : 'size-28 min-w-28'
              } rounded-xl`}
              textClassName="text-xl font-semibold text-cofeeNeutral10"
              bgColor={theme.cofeePurple1}
            />
            {previewUrl && <LogoDeleteIcon onDelete={deleteFile} />}
          </div>
          <div className="flex h-28 flex-col justify-between">
            <div className="mb-2">
              <span className="block text-sm font-semibold text-cofeeNeutral10">
                Business logo
              </span>
              <span className="block text-sm text-cofeeGreyBlue7">
                PNG or JPG files only (max 5MB, 295x295 preferred)
              </span>
            </div>
            <input
              type="file"
              id={inputId}
              className="hidden"
              onChange={onFileSelect}
              accept={acceptedLogoInputTypes}
              ref={fileInputRef}
            />
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                label={`${previewUrl ? 'Change logo' : 'Add logo'}`}
                handleClick={() => {
                  onChangeLogoClick?.();
                  fileInputRef.current?.click();
                }}
                className="w-fit !bg-[#F5FCF8]"
                leftIconName="image"
              />
            </div>
          </div>
          <div className="hidden items-center gap-x-5 rounded-xl border border-gray-300 p-3">
            <div className="flex items-center justify-center rounded-[5px]">
              <img
                src={previewUrl}
                className="object-fit h-25 w-auto rounded-[5px]"
                alt="branch_logo"
              />
            </div>
            <div>
              <div className="items-center justify-center gap-2">
                <div className="mt-2 flex justify-center">
                  <div
                    className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-red-50 px-4 py-2 text-sm font-medium text-red-500 transition hover:bg-red-100"
                    role="presentation"
                    onClick={deleteFile}
                  >
                    <Icon
                      name="trash-red-2"
                      size="none"
                      className="size-5 text-red-500"
                    />
                    Remove
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {fileDropError && (
          <div className="left-0 top-28 rounded text-sm text-cofeeRed7">
            {fileDropError}
          </div>
        )}
      </div>

      {openImageCropModal && (
        <ImageCropModal
          onClose={onCancelCrop}
          onConfirm={onConfirmCrop}
          fileName={fileName}
          previewUrl={fileUrl || ''}
          initialRatio={ratio}
        />
      )}
    </div>
  );
};
export default LogoUpload;
